import React from "react";

// Small visual kit shared by every tab of the collaborator space. Colours are
// solid and high-contrast (the previous glass cards washed the text out), and
// every piece works from a 360 px phone up.

export function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        {eyebrow ? <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#5f7d68]">{eyebrow}</p> : null}
        <h1 className="mt-1 font-serif text-3xl leading-tight text-[#211d19] md:text-4xl">{title}</h1>
        {subtitle ? <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#5b5247]">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}

export function Card({ className = "", children, ...rest }) {
  return (
    <section className={`rounded-3xl border border-[#e9e1d4] bg-white p-5 shadow-[0_18px_40px_-34px_rgba(83,64,52,0.5)] md:p-6 ${className}`} {...rest}>
      {children}
    </section>
  );
}

const TONES = {
  success: { chip: "bg-emerald-50 text-emerald-800 ring-emerald-200", dot: "bg-emerald-500", bar: "#4f9d6e" },
  warning: { chip: "bg-amber-50 text-amber-800 ring-amber-200", dot: "bg-amber-500", bar: "#e0a53a" },
  danger: { chip: "bg-rose-50 text-rose-800 ring-rose-200", dot: "bg-rose-500", bar: "#d6616f" },
  info: { chip: "bg-sky-50 text-sky-800 ring-sky-200", dot: "bg-sky-500", bar: "#4a90b8" },
  neutral: { chip: "bg-stone-100 text-stone-700 ring-stone-200", dot: "bg-stone-400", bar: "#a8a095" },
};
export const toneBar = (tone) => (TONES[tone] || TONES.neutral).bar;

export function Pill({ tone = "neutral", children, className = "" }) {
  const style = TONES[tone] || TONES.neutral;
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset ${style.chip} ${className}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} aria-hidden="true" />
      {children}
    </span>
  );
}

export function StatCard({ label, value, hint, tone = "neutral", icon: Icon }) {
  const accent = { success: "text-emerald-700 bg-emerald-50", warning: "text-amber-700 bg-amber-50", danger: "text-rose-700 bg-rose-50", info: "text-sky-700 bg-sky-50", neutral: "text-[#5f7d68] bg-[#eef3ec]" }[tone] || "text-[#5f7d68] bg-[#eef3ec]";
  return (
    <div className="rounded-2xl border border-[#e9e1d4] bg-white p-4 shadow-[0_14px_30px_-28px_rgba(83,64,52,0.55)]">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-bold uppercase tracking-wider text-[#6a6055]">{label}</p>
        {Icon ? <span className={`flex h-8 w-8 items-center justify-center rounded-xl ${accent}`}><Icon className="h-4 w-4" aria-hidden="true" /></span> : null}
      </div>
      <p className="mt-2 font-serif text-3xl leading-none text-[#211d19]">{value}</p>
      {hint ? <p className="mt-1.5 text-xs text-[#6a6055]">{hint}</p> : null}
    </div>
  );
}

/** Stacked bar. `segments`: [{ value, tone, label }]. Zero-width segments are skipped. */
export function SegmentedBar({ segments, height = "h-2.5", label }) {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);
  return (
    <div
      className={`flex w-full overflow-hidden rounded-full bg-stone-100 ${height}`}
      role="img"
      aria-label={label || segments.map((segment) => `${segment.label}: ${segment.value}`).join(", ")}
    >
      {total > 0 ? segments.filter((segment) => segment.value > 0).map((segment) => (
        <div key={segment.label} style={{ width: `${(segment.value / total) * 100}%`, backgroundColor: toneBar(segment.tone) }} />
      )) : null}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, text }) {
  return (
    <div className="flex flex-col items-center rounded-2xl border border-dashed border-[#d9cfbf] bg-[#fbf8f2] px-6 py-10 text-center">
      {Icon ? <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-[#8b7f70] shadow-sm"><Icon className="h-6 w-6" aria-hidden="true" /></span> : null}
      <p className="font-serif text-lg text-[#211d19]">{title}</p>
      {text ? <p className="mt-1 max-w-sm text-sm text-[#5b5247]">{text}</p> : null}
    </div>
  );
}

export function SkeletonBlock({ className = "" }) {
  return <div className={`animate-pulse rounded-2xl bg-stone-200/70 ${className}`} aria-hidden="true" />;
}

export function TabSkeleton() {
  return (
    <div className="w-full space-y-5" role="status" aria-label="Chargement">
      <SkeletonBlock className="h-24" />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[0, 1, 2, 3].map((index) => <SkeletonBlock key={index} className="h-24" />)}
      </div>
      <SkeletonBlock className="h-64" />
    </div>
  );
}

const AVATAR_TONES = ["bg-[#e6eee4] text-[#3f5a48]", "bg-[#f3e6dc] text-[#7a4f35]", "bg-[#e5ebf3] text-[#3d5a7a]", "bg-[#f1e4ec] text-[#7a3f5f]", "bg-[#efe9d6] text-[#6b5a22]"];
export function Avatar({ name, seed = "" }) {
  const initials = String(name || "?").trim().split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase() || "").join("") || "?";
  const hash = Array.from(String(seed || name)).reduce((sum, character) => sum + character.charCodeAt(0), 0);
  return <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold ${AVATAR_TONES[hash % AVATAR_TONES.length]}`} aria-hidden="true">{initials}</span>;
}
